import { useEffect, useState } from 'react';
import { navService, type NavCounts } from '@services/nav.service';
import { webSocketService, type WebSocketEvents } from '@hooks/useWebSocket';

/** Background poll; live events below catch most changes sooner. */
export const NAV_COUNTS_POLL_MS = 30_000;
/** Events arrive in bursts (one ticket save can emit several); refresh once after they settle. */
export const NAV_COUNTS_EVENT_DEBOUNCE_MS = 1_000;

/** Live events that can change a badge: ticket assignment / status / SLA, and the chat queue. */
export const NAV_COUNT_EVENTS: (keyof WebSocketEvents)[] = [
  'ticket:created',
  'ticket:updated',
  'ticket:assigned',
  'sla:warning',
  'chat:session:new',
  'chat:session:assigned',
  'chat:session:ended',
];

/**
 * Badge counts for the nav rail. Loads on mount, polls every
 * NAV_COUNTS_POLL_MS while the tab is visible (a hidden tab doesn't poll),
 * refreshes when the tab comes back, and re-fetches fresh — past the BFF's
 * short cache — shortly after a live event that can change a count.
 *
 * null until the first load. A failed refresh keeps the last counts rather
 * than blanking every badge.
 */
export function useNavCounts(): NavCounts | null {
  const [counts, setCounts] = useState<NavCounts | null>(null);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let debounce: number | undefined;

    const load = async (fresh = false) => {
      if (document.visibilityState === 'hidden') return;
      if (inFlight) {
        // An event landed mid-request; that request may predate it, so go again.
        if (fresh) refreshSoon();
        return;
      }
      inFlight = true;
      try {
        const next = await navService.getCounts(fresh);
        if (!cancelled) setCounts(next);
      } catch {
        // Keep the last counts; the next poll tries again.
      } finally {
        inFlight = false;
      }
    };

    const refreshSoon = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void load(true), NAV_COUNTS_EVENT_DEBOUNCE_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };

    void load();
    const poll = window.setInterval(() => void load(), NAV_COUNTS_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const unsubscribe = NAV_COUNT_EVENTS.map((event) => webSocketService.subscribe(event, refreshSoon));

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.clearTimeout(debounce);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      unsubscribe.forEach((off) => off());
    };
  }, []);

  return counts;
}
